import * as React from 'react';
import { MdClose, MdHub, MdRequestQuote, MdSearch, MdTaskAlt, MdVisibility } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import DataGrid from '../../Components/Table/DataGrid';
import { APPRoutes } from '../../Utils/Route';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { toast } from 'react-toastify';
import PageTitle from '../../Components/Common/PageTitle';
import { getFeeSchedulePayers } from '../../Services/FeeScheduleAnalyticsService';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const EMPTY_SUMMARY = { total_payers: 0, total_networks: 0, completed_networks: 0 };

export default function FeeScheduleAnalyticsList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canList = permissionHas(userPermissionCodes, PermissionCodes.FEE_SCHEDULE_ANALYTICS_LIST, decodedToken);
  const canViewDetails = permissionHas(userPermissionCodes, PermissionCodes.FEE_SCHEDULE_ANALYTICS_VIEW, decodedToken);

  const [rows, setRows] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [summary, setSummary] = React.useState(EMPTY_SUMMARY);

  const load = React.useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await getFeeSchedulePayers(token);
      if (res?.is_success && res.data) {
        setRows(Array.isArray(res.data.payers) ? res.data.payers : []);
        const s = res.data.summary || {};
        setSummary({
          total_payers: num(s.total_payers),
          total_networks: num(s.total_networks),
          completed_networks: num(s.completed_networks),
        });
      } else {
        setRows([]);
        setSummary(EMPTY_SUMMARY);
        if (res?.message) toast.error(res.message);
      }
    } catch (error) {
      console.error('Error loading fee schedule payers:', error);
      setRows([]);
      setSummary(EMPTY_SUMMARY);
      toast.error('Unable to load fee schedule payers.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    load();
  }, [load]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (row) => (row.name || '').toLowerCase().includes(term) || (row.code || '').toLowerCase().includes(term),
    );
  }, [rows, search]);

  const stats = React.useMemo(
    () => [
      {
        label: 'Payers',
        value: summary.total_payers,
        icon: <MdRequestQuote size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Networks',
        value: summary.total_networks,
        icon: <MdHub size={22} color="var(--hc-icon-amber-text)" />,
        iconBg: 'var(--hc-icon-amber-bg)',
        accentClass: 'border-warning',
        valueClass: 'text-warning',
      },
      {
        label: 'Completed Networks',
        value: summary.completed_networks,
        icon: <MdTaskAlt size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
    ],
    [summary],
  );

  const goToView = React.useCallback(
    (row) => {
      if (!canViewDetails) return;
      navigate(APPRoutes.FEE_SCHEDULE_ANALYTICS_VIEW.replace(':id', row.insurance_id), { state: { payer: row } });
    },
    [navigate, canViewDetails],
  );

  const columns = React.useMemo(
    () => [
      {
        id: 'name',
        label: 'Payer',
        minWidth: 220,
        filterable: true,
        format: (value, row) =>
          canViewDetails ? (
            <button
              type="button"
              className="btn p-0 text-start text-dark border-0 bg-transparent"
              style={{ font: 'inherit' }}
              onClick={() => goToView(row)}
            >
              {value || '—'}
            </button>
          ) : (
            <span>{value || '—'}</span>
          ),
      },
      { id: 'code', label: 'Code', minWidth: 90, align: 'center', filterable: true, format: (value) => value || <span className="text-secondary">—</span> },
      { id: 'network_count', label: 'Networks', minWidth: 110, align: 'center', format: (value) => num(value) },
      { id: 'completed_network_count', label: 'Completed', minWidth: 110, align: 'center', format: (value) => num(value) },
      { id: 'failed_network_count', label: 'Failed', minWidth: 90, align: 'center', format: (value) => num(value) },
      ...(canViewDetails ? [{
        id: 'actions',
        label: 'Action',
        minWidth: 110,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <button
              type="button"
              title="View"
              className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
              style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
              onClick={() => goToView(row)}
            >
              <MdVisibility size={16} />
            </button>
          </div>
        ),
      }] : []),
    ],
    [canViewDetails, goToView],
  );

  if (!canList) {
    return (
      <div className="container-fluid py-4">
        <PageTitle title="Fee Schedule Analytics | Aegis Health 360" />
        <div className="alert alert-danger">You do not have permission to view Fee Schedule Analytics.</div>
      </div>
    );
  }

  return (
    <div>
      <PageTitle title="Fee Schedule Analytics - Aegis Health 360" />
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Fee Schedule Analytics</h4>
          <p className="text-secondary mb-0 small">
            Select a payer to explore its negotiated rates, networks, and in-network providers.
          </p>
        </div>
      </div>

      <div className="row g-3 mb-3">
        {stats.map((stat) => (
          <div key={stat.label} className="col-12 col-sm-6 col-lg-4 col-xl">
            <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
              <div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`} />
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-3"
                style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
              >
                {stat.icon}
              </div>
              <div className="d-flex flex-column fw-bold lh-sm">
                <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                  {stat.label}
                </span>
                <span className={`fw-bold ${stat.valueClass} fs-5`}>{stat.value}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3 align-items-center">
          <div className="col-12">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input w-100"
                placeholder="Search by payer name or code..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: search ? '36px' : '12px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <DataGrid
          columns={columns}
          rows={filteredRows}
          loading={loading}
          skeletonRows={6}
          defaultRowsPerPage={10}
          stickyHeader
          getRowId={(row) => row.insurance_id}
        />
      </div>
    </div>
  );
}
